import React, { ReactNode, useMemo, useRef } from 'react'
import { Transition } from 'react-transition-group'
import {
	ColorsSchemeNames,
	renderAppearance,
	renderDecrease,
	renderDisclosure,
	renderIncrease,
	renderSliding,
} from '@rst/ui-ts'

import { useEnableAnimation } from '../lib/enable-animation'
import { isBlockAnimation } from './is-block-animation'
import { Animations } from '../../../../types'

type BlockAnimationCoreProps = {
	animation?: Animations.AnimationParams
	bgColor?: ColorsSchemeNames | string
	bgImage?: string
	children?: ReactNode
	inProp?: boolean
	mountOnEnter?: boolean
	className?: string
}

export const BlockAnimationCore = ({
	animation,
	bgColor,
	bgImage,
	children,
	inProp,
	mountOnEnter,
	className,
}: BlockAnimationCoreProps) => {
	const { enabled } = useEnableAnimation()

	const ref = useRef<HTMLDivElement>(null!)

	const renderAnimation = useMemo(() => {
		if (!isBlockAnimation(animation?.type)) return null
		const type = animation?.type as Animations.AnimationType
		if (type === 'increase') return renderIncrease
		if (type === 'decrease') return renderDecrease
		if (type === 'appearance') return renderAppearance
		if (type === 'sliding') return renderSliding
		if (type === 'disclosure') return renderDisclosure
		return null
	}, [animation?.type])

	const timeout = useMemo(() => {
		const time = (getTime(animation?.time) + getTime(animation?.delay)) * 1000

		return { appearance: time, enter: time, exit: 0 }
	}, [animation?.time, animation?.delay])

	if (
		!enabled ||
		!renderAnimation ||
		!animation?.type ||
		animation.type === 'none'
	)
		return <>{children}</>

	return (
		<Transition
			timeout={timeout}
			nodeRef={ref}
			in={inProp}
			mountOnEnter={mountOnEnter}
			appear
		>
			{renderAnimation({
				bgColor: bgColor as ColorsSchemeNames,
				children,
				ref,
				bgImage,
				className,
				...animation,
			})}
		</Transition>
	)
}

const getTime = (time?: string | number) => {
	if (!time) return 0
	if (typeof time === 'number') return time
	const num = parseFloat(time)
	if (isNaN(num)) return 0
	return num
}
